import React from 'react';
import LegalDocLayout from '../../components/ChinaDolphins/LegalDocLayout/LegalDocLayout';

const TOC = [
  { id: 'vision-philosophy', label: '1. Vision and Core Philosophy' },
  { id: 'prohibitions-red-lines', label: '2. General Prohibitions and Red Lines' },
  { id: 'stakeholder-duties', label: '3. Stakeholder Responsibilities' },
  { id: 'enforcement-actions', label: '4. Enforcement and Escalation' },
  { id: 'declaration-commitment', label: '5. Declaration of Commitment' },
];

export const CodeOfEthicsContent = () => (
  <div>
    <p>
      <strong>ChinaDolphins</strong> is built on the foundational principles of <em>trust, transparency, and technology</em>.
      We believe that international commerce must never prosper at the expense of human dignity, social responsibility, or ethical manufacturing standards.
    </p>
    <p>
      All registered Global Buyers, verified Sourcing Agents, and Manufacturing Partners are strictly required to adhere to this Global Code of Ethics as an unconditional condition of participation.
    </p>

    <h2 id="vision-philosophy">1. Vision and Core Philosophy</h2>
    <p>
      Our mission is to democratize global supply chains while championing fair labor, environmental responsibility, and commercial integrity. We maintain zero tolerance for exploitative practices across all tiers of production.
    </p>

    <h2 id="prohibitions-red-lines">2. General Prohibitions and Red Lines</h2>
    <p>
      The following items and practices are categorically banned from being requested, manufactured, listed, aggregated, or transacted on ChinaDolphins:
    </p>

    <h3>2.1 Child Labor &amp; Modern Slavery</h3>
    <p>
      Manufacturing partners must never employ child labor, forced labor, bonded labor, prison labor, or human trafficking practices. Factories must provide safe, hygienic working conditions, respect statutory working hour limits, and pay fair wages in compliance with applicable labor laws.
    </p>

    <h3>2.2 Adult &amp; Explicit Products</h3>
    <p>
      Adult novelty items, explicitly sexual goods, and fetish/sensual products whose primary purpose is sexualization or objectification are strictly prohibited from the catalogue.
    </p>

    <h3>2.3 Exploitative or Demeaning Marketing</h3>
    <p>
      All product photography, specifications, and marketing assets must maintain high professional standards. Media that objectifies individuals or displays degrading content is forbidden.
    </p>

    <h3>2.4 Legally Restricted or Prohibited Goods</h3>
    <p>
      Any merchandise whose production, export, import, sale, or possession is restricted or unlawful under the laws of Mainland China, Hong Kong, or the destination market is prohibited.
    </p>

    <h2 id="stakeholder-duties">3. Stakeholder Responsibilities</h2>
    <h3>3.1 Manufacturing Factories</h3>
    <ul>
      <li>Maintain honest, audited production practices and verifiable environmental/labor compliance.</li>
      <li>Provide authentic material composition data, quality certificates, and test reports.</li>
      <li>Refuse the manufacture of counterfeit, infringing, or dangerous items.</li>
    </ul>

    <h3>3.2 Verified Sourcing Agents</h3>
    <ul>
      <li>Conduct diligent on-site factory vetting and inspect working environments.</li>
      <li>Actively screen and reject inappropriate, prohibited, or suspicious product lines.</li>
      <li>Immediately escalate any detected labor, environmental, or ethical violations to ChinaDolphins Compliance.</li>
    </ul>

    <h3>3.3 Global Buyers</h3>
    <ul>
      <li>Never submit RFQs, aggregation pools, or sample requests for banned or restricted items.</li>
      <li>Respect fair trade terms and engage ethically with overseas partners.</li>
    </ul>

    <h2 id="enforcement-actions">4. Enforcement and Escalation</h2>
    <p>
      Violations of this Code of Ethics trigger immediate compliance remedies:
    </p>
    <ul>
      <li><strong>Immediate Account Termination:</strong> Permanent ban of offending buyer, agent, or factory accounts.</li>
      <li><strong>Compliance Review:</strong> Transaction or account activity may be paused or placed under compliance review where permitted.</li>
      <li><strong>Regulatory Reporting:</strong> Escalation of serious violations (such as forced labor or contraband) to competent law enforcement and international trade regulatory bodies.</li>
    </ul>

    <h2 id="declaration-commitment">5. Declaration of Commitment</h2>
    <p>
      By registering and maintaining an active account on ChinaDolphins, every participant enters into a binding covenant to conduct trade with transparency, respect for human rights, and unwavering ethical responsibility.
    </p>
    <div style={{ background: '#f0fdf4', padding: '16px', borderRadius: '8px', border: '1px solid #bbf7d0', marginTop: '16px' }}>
      <p style={{ margin: 0, fontWeight: 600, color: '#166534' }}>
        Mandatory Registration Agreement: &quot;I have read and agree to follow the ChinaDolphins Global Code of Ethics.&quot;
      </p>
    </div>
  </div>
);

const CodeOfEthicsPage = () => {
  return (
    <LegalDocLayout
      title="Global Code of Ethics & Social Responsibility"
      subtitle="Foundational ethical benchmarks, zero-tolerance prohibitions, and fair labor standards for the ChinaDolphins ecosystem."
      effectiveDate="January 1, 2026"
      lastUpdated="August 13, 2026"
      jurisdiction="Hong Kong / Global (MTN GLOBAL LIMITED)"
      tocItems={TOC}
    >
      <CodeOfEthicsContent />
    </LegalDocLayout>
  );
};

export default CodeOfEthicsPage;
